import { useEffect, useState } from "react"
import "katex/dist/katex.min.css"
import "./app.css"
import { useSolver } from "./hooks/useSolver.js"
import { useConfig } from "./hooks/useConfig.js"
import { useHistory } from "./hooks/useHistory.js"
import {
  EXAMPLES,
  LEVELS,
  IS_WEB,
  answerText,
  regionForLevel,
  recognizeEquation,
  getCroppedImg,
  validateImageFile,
} from "./utils.js"
import { track } from "./analytics.js"
import Header from "./Header.jsx"
import MainPage from "./MainPage.jsx"
import SolvePage from "./SolvePage.jsx"
import ErrorView from "./ErrorView.jsx"

function computePhase(solver, isEmptyDone) {
  if (isEmptyDone || solver.status === "ERROR") return "ERROR"
  if (solver.status === "PENDING" || solver.status === "STREAMING" || solver.status === "DONE") return "SOLVE"
  return "MAIN"
}

// Match on tab id (registered by the welcome page); tab.url is hidden for
// extension pages without the "tabs" permission.
async function isWelcomeTab(tabId) {
  if (typeof chrome === "undefined" || !chrome.storage?.session) return false
  const { welcomeTabs = [] } = await chrome.storage.session.get({ welcomeTabs: [] })
  return welcomeTabs.includes(tabId)
}

export default function App() {
  const { config, setLevel, setRegion } = useConfig()
  const { history, push: pushHistory, clear: clearHistory } = useHistory()
  const solver = useSolver()
  const [problem, setProblem] = useState("")
  const [inputSource, setInputSource] = useState("input")
  const setProblemFrom = (text, source) => {
    setProblem(text)
    setInputSource(source)
  }
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotSource, setScreenshotSource] = useState("screenshot")
  const setScreenshotFrom = (dataUrl, source) => {
    setScreenshot(dataUrl)
    setScreenshotSource(source)
  }
  const [recognizing, setRecognizing] = useState(false)
  const [attachError, setAttachError] = useState(null)

  useEffect(() => {
    try {
      const handler = async (message) => {
        if (message.type === "solve-selection" && message.text) setProblemFrom(message.text, "selection")
        if (message.type === "manual-crop" && message.dataUrl) setScreenshotFrom(message.dataUrl, "screenshot")
        if (message.type === "auto-crop" && message.dataUrl && message.rect) {
          setRecognizing(true)
          try {
            const cropped = await getCroppedImg(message.dataUrl, message.rect)
            const equation = await recognizeEquation(cropped)
            track("recognize", { source: "auto-crop" })
            setProblemFrom(equation, "screenshot")
          } catch (err) {
            track("recognize_failed", { source: "auto-crop", message: err?.message })
          } finally {
            setRecognizing(false)
          }
        }
        if (message.type === "element-picked") {
          if (message.text) setProblemFrom(message.text, "pick")
          else if (message.imageSrc) setScreenshotFrom(message.imageSrc, "pick")
        }
      }
      chrome.runtime.onMessage.addListener(handler)
      return () => chrome.runtime.onMessage.removeListener(handler)
    } catch {}
  }, [])

  const isEmptyDone = solver.status === "DONE" && !answerText(solver.answer) && solver.steps.length === 0
  const phase = computePhase(solver, isEmptyDone)

  useEffect(() => {
    if (solver.status !== "DONE" || !solver.problem || isEmptyDone) return
    const matched = EXAMPLES.find((example) => example.equation === solver.problem)
    const solveRegion = regionForLevel(solver.level)
    pushHistory({
      equation: solver.problem,
      level: solver.level,
      levelLabel: LEVELS[solveRegion]?.[solver.level]?.label || solver.level,
      region: solveRegion,
      answer: answerText(solver.answer),
      answerData: solver.answer,
      steps: solver.steps,
      accent: matched?.accent || "purple",
      iconKey: matched?.iconKey || "inequality",
    })
  }, [solver.status, solver.problem, solver.level, solver.answer, solver.steps, pushHistory, isEmptyDone])

  const handleSubmit = () => {
    if (!problem.trim() || !config) return
    console.log("[EqSolve app] submit", { problem: problem.trim(), level: config.level, region: config.region })
    solver.run(problem.trim(), config.level, inputSource)
  }

  const handleBack = () => {
    console.log("[EqSolve app] back (keeps input)", { problem })
    solver.reset()
  }

  const handleNewQuestion = () => {
    console.log("[EqSolve app] new question")
    solver.reset()
    setProblem("")
    setScreenshot(null)
  }

  const handleHistoryClick = (entry) => {
    if (!problem.trim()) setProblem(entry.equation)
    const entryRegion = entry.region && LEVELS[entry.region] ? entry.region : null
    const validLevel = entryRegion && entry.level && LEVELS[entryRegion]?.[entry.level] ? entry.level : null
    const cacheable = Array.isArray(entry.steps) && entry.answerData && validLevel
    console.log("[EqSolve app] history click", {
      equation: entry.equation,
      region: entryRegion,
      level: validLevel,
      mode: cacheable ? "cache" : "run",
    })
    if (cacheable) {
      solver.loadCached({
        problem: entry.equation,
        level: validLevel,
        steps: entry.steps,
        answer: entry.answerData,
      })
    } else {
      solver.run(entry.equation, validLevel || config?.level, "history")
    }
  }

  const handleExampleClick = (entry) => {
    console.log("[EqSolve app] example click (prefill only)", { equation: entry.equation })
    setProblemFrom(entry.equation, "example")
  }

  const handleRetry = () => {
    if (solver.problem && solver.level) solver.run(solver.problem, solver.level, "retry")
  }

  // Welcome page can't be injected, so it captures itself via a direct message.
  const startCapture = async (captureMode, injectType) => {
    try {
      const [tab] = await chrome.tabs.query({ active: true, currentWindow: true })
      if (!tab?.id) return
      if (await isWelcomeTab(tab.id)) {
        chrome.runtime.sendMessage({ type: "start-self-capture", mode: captureMode })
      } else {
        chrome.runtime.sendMessage({ type: injectType, tabId: tab.id })
      }
    } catch {}
  }

  const handleStartCapture = () => {
    track("screenshot")
    startCapture("pickRegion", "start-capture")
  }
  const handlePickElement = () => {
    track("pick")
    startCapture("pickElement", "start-pick")
  }

  const handleAttach = async (file) => {
    setAttachError(null)
    try {
      const dataUrl = await validateImageFile(file)
      track("attach", { file_type: file.type, size: file.size })
      setScreenshotFrom(dataUrl, "attach")
    } catch (err) {
      setAttachError(err.message || "Invalid image file.")
    }
  }

  const handleCropDone = async (croppedBase64) => {
    try {
      const text = await recognizeEquation(croppedBase64)
      track("recognize", { source: "crop" })
      setProblemFrom(text, screenshotSource)
      setScreenshot(null)
    } catch (err) {
      track("recognize_failed", { source: "crop", message: err?.message })
    }
  }

  const handleCropCancel = () => {
    setScreenshot(null)
  }

  const handleSetLevel = (level) => {
    setLevel(level)
    track("settings_changed", {
      changed: "level",
      level,
      region: config?.region,
      $set: { level, region: config?.region },
    })
  }

  const handleSetRegion = (region) => {
    setRegion(region)
    track("settings_changed", { changed: "region", region, $set: { region } })
  }

  return (
    <div className="app-shell">
      <div className="aurora-bg" aria-hidden="true" />
      <Header
        config={config}
        onSetLevel={handleSetLevel}
        onSetRegion={handleSetRegion}
        onBack={phase !== "MAIN" ? handleBack : null}
        disabled={phase !== "MAIN"}
      />
      {__DEBUG__ && IS_WEB && (
        <a className="golden-pin" href="#/golden" title="golden cases">
          ⚖
        </a>
      )}
      {phase === "MAIN" && (
        <MainPage
          config={config}
          history={history}
          problem={problem}
          setProblem={(value) => setProblemFrom(value, "input")}
          onSubmit={handleSubmit}
          onHistoryClick={handleHistoryClick}
          onExampleClick={handleExampleClick}
          onClearHistory={clearHistory}
          solverStatus={solver.status}
          onScreenshot={handleStartCapture}
          onAttach={handleAttach}
          onPick={handlePickElement}
          screenshot={screenshot}
          onCropDone={handleCropDone}
          onCropCancel={handleCropCancel}
          recognizing={recognizing}
          attachError={attachError}
          onClearAttachError={() => setAttachError(null)}
        />
      )}
      {phase === "SOLVE" && (
        <SolvePage
          problem={solver.problem}
          steps={solver.steps}
          answer={solver.answer}
          status={solver.status}
          level={solver.level}
          onNewQuestion={handleNewQuestion}
        />
      )}
      {phase === "ERROR" && (
        <ErrorView
          kind={isEmptyDone ? "UNRECOGNIZED" : solver.error?.kind}
          onRetry={handleRetry}
          onNewQuestion={handleNewQuestion}
        />
      )}
    </div>
  )
}
